<template>
  <MainLayout>
    <div class="container mw-960" slot="action-bar">
      <div class="row">
        <div class="col-md-6 col-lg-7 col-xl-8">
          <h5 class="action-header typography--headline typography--color-secondary pt-1 pl-10-mobile pr-10-mobile">
            {{ cardTitle }}
          </h5>
        </div>

        <div class="col-md-6 col-lg-5 col-xl-4 hidden-sm-down">
          <div class="action-btn-row">
            <button class="btn btn-outline-primary float-right mr-1" v-on:click="openCardPreview">{{ $t('common.preview') }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-960 mt-4 pt-1" slot="layout-body">
      <div class="row">
        <div class="col-md-4" v-if="!card">
          <div class="card-row">
            <DummyCard></DummyCard>
          </div>
        </div>

        <div class="col-md-4" v-else>
          <div class="card-row">
            <Card class="library-detail-card">
              <div class="card-cover-img-thumb" v-bind:style="styleBindings"></div>
              <div class="card-block">
                <div class="card-info-section">
                  <div class="row">
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.subject') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getSubjectLabel(card) }}
                      </small>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.grade') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getGradeLabel(card) }}
                      </small>
                    </div>
                  </div>
                  <!-- Chapter -->
                  <div class="row">
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.chapter') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary" v-if="card.chapter_name">
                        {{ card.chapter_name }}
                      </small>
                      <small class="typography--body-3 typography--color-disabled" v-else>
                        -
                      </small>
                    </div>
                  </div>
                  <!-- Core Concept -->
                  <div class="row">
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.core_concept') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary" v-if="card.core_concept_id">
                        {{ getCoreConceptLabel(card.core_concept) }}
                      </small>
                      <small class="typography--body-3 typography--color-disabled" v-else>
                        -
                      </small>
                    </div>
                  </div>
                </div>

                <div class="card-keywords-section">
                  <div class="row">
                    <div class="col-3">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.learning_elements') }}
                      </small>
                    </div>
                    <div class="col-9">
                      <small class="typography--body-3 typography--color-primary" v-if="card.core_concept_id">
                        {{ getLearningElements(card.core_concept) }}
                      </small>
                      <small class="typography--body-3 typography--color-disabled" v-else>
                        -
                      </small>
                    </div>
                  </div>
                </div>

              </div>

              <div class="card-bottom-desc">
                <small class="typography--caption typography--color-secondary">
                  {{ $t('common.card.non_quiz') }} {{ $t('common.card.count', { cnt: card.page_count - card.quiz_count }) }} ∙
                  {{ $t('common.card.quiz') }} {{ $t('common.card.count', { cnt: card.quiz_count }) }}
                </small>
                <small class="typography--caption typography--color-secondary">
                  <strong>{{ cardAuthorName }}</strong>
                  <span v-if="card.assigned_count">| {{ $t('common.card.share_count', { cnt: card.assigned_count }) }}</span>
                </small>
                <small v-if="!card.publisher_id" class="typography--caption typography--color-secondary">
                  {{ $t('common.card.updated_at') }}: {{ card.updated_at | datetime }}
                </small>
              </div>

            </Card>
          </div>

        </div>


        <div class="col-md-8" v-if="!pagesReady">
          <div class="card-row">
            <DummyPageSummary></DummyPageSummary>
          </div>
        </div>

        <div class="col-md-8" v-else>
          <div class="card-row" v-for="(page, index) in card.pages" v-bind:key="page.id">
            <PageSummary
              v-bind:page="page"
              v-bind:index="index"
            >
            </PageSummary>
          </div>
        </div>
      </div>
    </div>

    <div slot="modal-area">
      <b-modal id="no-class-to-assign-alert"
               class="basic-modal no-header-modal hide-cancel-btn"
               v-bind:hide-header="true"
               v-bind:ok-title="$t('common.confirm')">
        <div v-html="$t('common.modal.no_class_to_assign_alert.message')"></div>
      </b-modal>
    </div>

    <div slot="floating-action-menu" class="hidden-md-up">
      <FloatingDropup>
        <b-dropdown-item v-on:click="openCardPreview">{{ $t('common.preview') }}</b-dropdown-item>
      </FloatingDropup>
    </div>

  </MainLayout>
</template>

<script>
  import { isEmpty } from 'lodash';
  import { mapGetters, mapActions } from 'vuex';
  import labels from '@/mixins/labels';
  import PageSummary from '../components/PageSummary';

  export default {
    name: 'card-details',

    components: {
      PageSummary,
    },

    mixins: [
      labels,
    ],

    data() {
      return {
        pagesReady: false,
        card: {},
      };
    },

    computed: {
      ...mapGetters([
        'publisherCards',
        'customCards',
        'subjectMap',
        'gradeMap',
      ]),

      isPublisherCard() {
        return this.$route.params.card_type === 'publisher-cards';
      },

      cardTitle() {
        if (!isEmpty(this.card)) {
          return this.card.title;
        }

        return '제목';
      },

      cardAuthorName() {
        if (!isEmpty(this.card)) {
          return this.card.author_name;
        }

        return '';
      },

      styleBindings() {
        if (!isEmpty(this.card)) {
          return {
            'background-image': `url('${this.card.cover_image}')`,
          };
        }

        return {};
      },
    },

    methods: {
      ...mapActions([
        'setSelectedPreviewPageIndex',
        'fetchPublisherCardPages',
        'setSelectedPreviewCard',
        'fetchCustomCardDetails',
        'fetchCustomCardPages',
      ]),

      getLearningElements(coreConcept) {
        if (!coreConcept) {
          return null;
        }

        return coreConcept.description;
      },

      openCardPreview() {
        this.setSelectedPreviewPageIndex(0)
          .then(() => {
            this.$root.$emit('show::modal', 'card-preview-modal');
          });
      },
    },

    created() {
      const cardId = this.$route.params.card_id;
      if (this.isPublisherCard) {
        this.card = this.publisherCards[cardId] || {};

        if (isEmpty(this.card)) {
          this.fetchCustomCardDetails(cardId)
            .then((card) => {
              if (card.share_level !== 1) {
                return Promise.reject('is_private_card');
              }

              this.card = card;
              return this.fetchPublisherCardPages(card.id);
            })
            .then((pages) => {
              this.card.pages = pages;

              return this.setSelectedPreviewCard(this.card);
            })
            .then(() => {
              this.pagesReady = true;
            })
            .catch((err) => {
              if (err === 'is_private_card') {
                return this.$router.replace('/library');
              }

              this.$root.$emit('show::modal', 'basic-error-alert');
              // this.$router.push('/library');
              return Promise.reject(err);
            });
        } else if (!this.card.pages) {
          this.fetchPublisherCardPages(this.card.id)
            .then(() => (
              this.setSelectedPreviewCard(this.card)
            ))
            .then(() => {
              this.pagesReady = true;
            })
            .catch((err) => {
              this.$root.$emit('show::modal', 'basic-error-alert');
              return Promise.reject(err);
            });
        } else {
          this.setSelectedPreviewCard(this.card)
            .then(() => {
              this.pagesReady = true;
            });
        }
      } else {
        this.card = this.customCards[cardId] || {};

        if (isEmpty(this.card)) {
          this.fetchCustomCardDetails(cardId)
            .then((card) => {
              if (card.share_level !== 1) {
                return Promise.reject('is_private_card');
              }

              this.card = card;

              return this.fetchCustomCardPages(card.id);
            })
            .then((pages) => {
              this.card.pages = pages;

              return this.setSelectedPreviewCard(this.card);
            })
            .then(() => {
              this.pagesReady = true;
            })
            .catch((err) => {
              if (err === 'is_private_card') {
                return this.$router.replace('/library');
              }

              this.$root.$emit('show::modal', 'basic-error-alert');
              // this.$router.push('/library');
              return Promise.reject(err);
            });
        } else if (!this.card.pages) {
          this.fetchCustomCardPages(this.card.id)
            .then(() => (
              this.setSelectedPreviewCard(this.card)
            ))
            .then(() => {
              this.pagesReady = true;
            })
            .catch((err) => {
              this.$root.$emit('show::modal', 'basic-error-alert');
              return Promise.reject(err);
            });
        } else {
          this.setSelectedPreviewCard(this.card)
            .then(() => {
              this.pagesReady = true;
            });
        }
      }
    },
  };
</script>



// WEBPACK FOOTER //
// src/app/library/views/CardDetails.vue